import { NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, Types } from '../../../App/Services/AppService';
import { Data } from '@singularsystems/neo-core';
import ClientAccountSearchCriteria from '../Models/ClientAccountSearchCriteria';
import { reaction } from 'mobx';
import RunInterceptionComponentVM from '../../../Common/Components/Interception/RunInterceptionComponentVM';
import MissingAccountsSearchCriteria from '../Models/MissingAccountsSearchCriteria';
import MissingAccountLookup from '../Models/MissingAccountLookup';
import { TextConstants } from '../../../Common/TextConstants';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import ExcludedAccountsVM from '../../ExclusionAccounts/Views/ExcludedAccountsVM';
import ExcludedAccountLookup from '../../ExclusionAccounts/Models/Lookups/ExcludedAccountLookup';
import CreateExcludedAccountCommand from '../../ExclusionAccounts/Models/Commands/CreateExcludedAccountCommand';
import ClientLookup from '../Models/ClientLookup';
import ManageClientAccountMappingVM from '../Components/ManageClientAccountMappingVM';
import CreateClientAccountMappingCommand from '../Models/CreateClientCommand';
import UpdateClientCommand from '../Models/UpdateClientCommand';
import ClientAccountLookup from '../Models/ClientAccountLookup';
import ClientAccount from '../Models/ClientAccount';
import CurrencyComponentVM from '../../../Common/Components/Currency/CurrencyComponentVM';
import StaticServiceProviderFileGroupingModel from '../../../Common/Models/StaticServiceProviderFileGroupingModel';


@NeoModel
export default class ClientAccountMappingVM extends Views.ViewModelBase {

    constructor(
        public clientAccountApiClient = AppService.get(Types.ApiClients.ClientAccountApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService))
        {
        super(taskRunner);
    }

    public missingAccountCriteria = new MissingAccountsSearchCriteria();
    public clientAccountSearchCriteria = new ClientAccountSearchCriteria();
    public runInterceptionVM = new RunInterceptionComponentVM();
    public showExcludeMissingAccountModalInd: boolean = false;
    public missingAccountToExclude = new MissingAccountLookup();
    public excludeAccount = new ExcludedAccountLookup();
    public missingAccountNumberToExclude: string = "";
    public showExcludedAccountsComponent: boolean = false;
    public excludedAccountsVM = new ExcludedAccountsVM();
    public manageClientAccountMappingVM = new ManageClientAccountMappingVM();

    public createClientAccountMappingCommand = new CreateClientAccountMappingCommand();
    public showManageClientAccountMappingComponent: boolean = false;
    public updateClientCommand = new UpdateClientCommand();
    public isClientUpdating: boolean = false;

    public async initialise() {
        reaction(() => this.clientAccountSearchCriteria.searchCriteria, () => this.clientAccountPageManager.refreshData(), {fireImmediately: false, delay: 650 });
    }

    public clientAccountPageManager = new Data.PageManager(this.clientAccountSearchCriteria, ClientLookup, this.clientAccountApiClient.getPagedClientAccounts, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
        afterFetch: this.setIsExpandedProp.bind(this),
    });

    public missingAccountPageManager = new Data.PageManager(this.missingAccountCriteria, MissingAccountLookup, this.clientAccountApiClient.getPagedMissingAccounts, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    });

    public setIsExpandedProp(data: ClientLookup[]) {
        data.forEach(client => {
            if (client.clientAccounts.length > 0) {
                client.isExpanded = false;
            }
        })
    }

    public backToClientAccountView() {
        
        this.showManageClientAccountMappingComponent = false;
        this.manageClientAccountMappingVM.isFieldReadOnlyInd = false;
        this.manageClientAccountMappingVM.missingAccountsReadOnlyInd = false;
        this.manageClientAccountMappingVM.currencyComponentVM = new CurrencyComponentVM();
        this.manageClientAccountMappingVM.selectedBroker = new StaticServiceProviderFileGroupingModel();
        this.manageClientAccountMappingVM.fileGroupingId = 0;
        this.initialise();
        this.manageClientAccountMappingVM.clientAccountModel = new ClientAccount();
    }

    public mapMissingAccount(missingAccountId?: number, fileGroupingId?: number, accountNumber?: string) {
        if (missingAccountId !== undefined) {
            this.manageClientAccountMappingVM.isFieldReadOnlyInd = false;
            this.manageClientAccountMappingVM.missingAccountId = missingAccountId!;
            this.manageClientAccountMappingVM.fileGroupingId = fileGroupingId!;
            this.manageClientAccountMappingVM.clientAccountModel.staticServiceProviderFileGroupingId = fileGroupingId!;
            this.manageClientAccountMappingVM.clientAccountModel.serviceProviderAccountNumber = accountNumber!;
            this.manageClientAccountMappingVM.missingAccountsReadOnlyInd = true;
        } else {
            this.manageClientAccountMappingVM.isFieldReadOnlyInd = false;
        }               

        this.showManageClientAccountMappingComponent = true;
        this.manageClientAccountMappingVM.initialise();
    }

    public async editClient(clientAccount: ClientAccountLookup, clientId: number) {
        var response = await this.clientAccountApiClient.getClientLookupById(clientId);
        var editClientDetails = response.data;
        this.manageClientAccountMappingVM.clientAccountModel.mapFrom(clientAccount);
        this.manageClientAccountMappingVM.clientAccountModel.mapFrom(editClientDetails);
        this.showManageClientAccountMappingComponent = true;
        this.manageClientAccountMappingVM.clientAccountModel.editingClientAccount = true;
        this.isClientUpdating = true;
        this.manageClientAccountMappingVM.isFieldReadOnlyInd = true;
        this.manageClientAccountMappingVM.initialise();  
    }

    public navigateToExcludedAccounts() {
        this.showExcludedAccountsComponent = true;
        this.excludedAccountsVM.initialise();
    }

    public showExcludeMissingAccountModal(missingAccount: MissingAccountLookup) {
        this.showExcludeMissingAccountModalInd = true;
        this.missingAccountNumberToExclude = missingAccount.accountReference!;
        this.missingAccountToExclude = missingAccount;
    }

    public closeExcludeMissingAccountModal() {
        this.showExcludeMissingAccountModalInd = false;
        this.missingAccountToExclude = new MissingAccountLookup();
    }

    public async excludeMissingAccount() {
        this.excludeAccount.accountNumber = this.missingAccountToExclude.accountReference!;
        this.excludeAccount.staticServiceProviderFileGroupingId = this.missingAccountToExclude.staticServiceProviderFileGroupingId!;
        await this.saveExcludedAccount(this.excludeAccount);
    }

    public clearSearch() {
        this.clientAccountSearchCriteria.meta.searchCriteria.value = "";
        this.clientAccountPageManager.refreshData();
    }

    public async saveClientAccount() {
        if (this.manageClientAccountMappingVM.clientAccountModel.clientAccountId === 0 || this.manageClientAccountMappingVM.clientAccountModel.clientAccountId === null) {
            this.mapCreateClientAccountMappingCommand();
            const response = await this.clientAccountApiClient.createClientAccountMapping(this.createClientAccountMappingCommand!.toJSObject());

            if (response.data.success) {
                NotifyUtils.add(TextConstants.Titles.Success, TextConstants.Notifications.AccountMappingSaved,
                    "success" as any, 10);
                
            } else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error,
                    "danger" as any, 10);
            }

        }

        if (this.isClientUpdating === true) {
            this.updateClientCommand.mapFrom(this.manageClientAccountMappingVM.clientAccountModel);
            const response = await this.clientAccountApiClient.updateAndSaveClient(this.updateClientCommand!.toJSObject());

            if (response.data.success) {
                NotifyUtils.add(TextConstants.Titles.Success, TextConstants.Notifications.AccountMappingSaved,
                    "success" as any, 10);
            } else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error,
                    "danger" as any, 10);
            }
        }
        this.manageClientAccountMappingVM.clientAccountModel = new ClientAccount();
        this.manageClientAccountMappingVM.missingAccountsReadOnlyInd = false;
        this.showManageClientAccountMappingComponent = false;
        this.isClientUpdating = false;
        this.manageClientAccountMappingVM.fileGroupingId = 0;
        this.manageClientAccountMappingVM.selectedBroker = new StaticServiceProviderFileGroupingModel();
        this.missingAccountPageManager.refreshData();
        this.clientAccountPageManager.refreshData();
    }

    private mapCreateClientAccountMappingCommand() {
        this.createClientAccountMappingCommand.mapFrom(this.manageClientAccountMappingVM.clientAccountModel);
        this.createClientAccountMappingCommand.isMappedFromMissingAccounts = this.manageClientAccountMappingVM.missingAccountId !== 0;
        this.createClientAccountMappingCommand.staticCurrencyId = this.manageClientAccountMappingVM.currencyComponentVM.selectedCurrency.staticCurrencyId;
        this.createClientAccountMappingCommand.missingAccountId = this.manageClientAccountMappingVM.missingAccountId ?? 0;
    }

    public async saveExcludedAccount(excludedAccount: ExcludedAccountLookup) {        
        this.taskRunner.waitFor(this.taskRunner.run(async () => {
            var response = await this.clientAccountApiClient.createExcludedAccount((this.mapExcludedAccountToCreateCommand(excludedAccount)!.toJSObject()));
            if (response.data.success) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.ExcludedAccountSaved, NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }

            await this.excludedAccountsVM.pageManager.refreshData();
            await this.missingAccountPageManager.refreshData();
        }))


        this.showExcludeMissingAccountModalInd = false;

        if (this.showExcludedAccountsComponent) {
            this.excludedAccountsVM.manageExclusionAccountComponentVM.managedExclusionAccount = new ExcludedAccountLookup();
            this.excludedAccountsVM.showManageExcludedAccountComponent = false;
        }
    }
    private mapExcludedAccountToCreateCommand(exclusionAccount: ExcludedAccountLookup) {
        var command = new CreateExcludedAccountCommand().mapFrom(exclusionAccount);
        command.staticServiceProviderFileGroupingID = exclusionAccount.staticServiceProviderFileGroupingId;
        return command;
    }
}